import React from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import qs from "qs";
import cn from "classnames";

import Icon from "components/Icon";
import PersonPreview from "components/PersonPreview";
import ConditionsFirst from "./ConditionsFirst";
import ConditionsSecond from "./ConditionsSecond";
import GameControls from "./GameControls";
import { gameTypes } from "domain/GamesSearch/types";
import { cityTypes, gameTypeTypes, levelTypes } from "domain/Directories/types";
import SignUp from "components/SignUpButton";
import PlayersRange from "components/PlayersRange";
import PlayersStack from "components/PlayersStack";
import PlayersList from "components/PlayersDetailing";
import YMapAddress from "components/YMapAddress";
import PlayersReport from "components/PlayersReport";
import { stickyHeader } from "utils";
import UserDetails from "./UserDetails";
import PlayerLoader from "components/Loaders/Player";
import ErrorBoundary from "components/ErrorBoundary";
import classNames from "classnames";
import Item from "components/NotificationsItem";
import { notificationsTypes } from "domain/Notifications/types";
import PlaygroundLoader from "components/Loaders/Playground";
import PlaygroundDetails from "components/PlaygroundDetails";
import GameDetailsInvitations from "./Invitations";
import GroupListLoader from "components/Loaders/GroupsList";
import BlackListLoader from "components/Loaders/BlackList";
import InternalServerError from "pages/InternalServerError";
import { getGenders, getFromGenders } from "domain/Directories/selectors";
import { get } from "domain/Game/api";
import AnnotationAgeLimit from "components/AnnotationAgeLimit";
import { DateTime as Luxon } from "luxon";
import Newsletter from "./Newsletter/Component";

export const SIDEBAR_ANIMATION_CLASS_OPENED = "sidebar-right-open";
export const SIDEBAR_ANIMATION_CLASS_CLOSED = "sidebar-right-close";
export const SIDEBAR_ANIMATION_DURATION = 300;
const MINUTES_INTERVAL = 10;
const HOURS_BEFORE_GAME = 3;

class GameDetails extends React.Component {
    constructor(props) {
        super(props);

        const searchParams = qs.parse(location.search, {
            ignoreQueryPrefix: true,
        });

        this.state = {
            showUser: searchParams.player,
            showPlayground: false,
            isPast: props.is_past,
            inProgress: props.inProgress,
            error: null,
            previousRoute: "/",
        };

        if (!props.is_past) {
            this.timeInterval = setInterval(
                this.recallGameWithoutLoader,
                MINUTES_INTERVAL * 1000
            );
        }
    }

    componentDidMount() {
        const { setViewed, unread_notifications, location } = this.props;
        stickyHeader("#card-header");
        window.scrollTo(0, 0);
        if (
            Array.isArray(unread_notifications) &&
            unread_notifications.length > 0
        ) {
            setViewed(unread_notifications.map((item) => item.id));
            this.needForget = true;
        }

        this.setState({
            previousRoute: location.state?.previousRoute,
        });
    }

    componentWillUnmount() {
        const { recall, id, private_code } = this.props;

        clearInterval(this.timeInterval);

        if (this.needForget) {
            recall({ id, code: private_code });
        }

        document.body.classList.remove(SIDEBAR_ANIMATION_CLASS_OPENED);
    }

    recallGameWithoutLoader = async () => {
        const { updateGameData, id, private_code } = this.props;

        this.hideError();
        try {
            const res = await get({ id, code: private_code });

            if (res) {
                updateGameData(id, res);
            }
        } catch (e) {
            this.onError(e);
        }
    };

    onError = (e) => {
        this.setState({ error: e.message });
    };

    hideError = () => {
        this.setState({ error: null });
    };

    handleGoBack = () => {
        if (this.state.previousRoute) {
            this.props.history.push(this.state.previousRoute);
        } else {
            this.props.history.push("/");
        }
    };

    renderButtons = () => {
        const {
            id,
            private_code,
            isOrganizer,
            isTrainer,
            need_report,
            gameLink,
            gameType,
            is_training,
            isMobile,
            genderName,
            profile,
            city,
            players_gender,
            age_limit,
            organizer,
            is_past,
        } = this.props;

        const { isPast, inProgress } = this.state;
        const genderDoesNotMatch =
            profile &&
            profile?.gender &&
            !getGenders()
                ?.find((gender) => gender.name === genderName)
                ?.code?.includes(profile?.gender);

        if (is_past && (isOrganizer || isTrainer)) {
            return need_report ? (
                <>
                    {!isMobile && (
                        <div className="mb-8">
                            <span className="fs-caption c-gs-medium-em">
                                Перед отправкой отчета проверьте список игроков
                                и уберите галочку с тех, кто не пришел
                            </span>
                        </div>
                    )}
                    <button
                        type="submit"
                        form="makeGameReport"
                        className="btn btn--medium btn--primary btn--on-top"
                    >
                        <span className="btn__span">Отправить отчет</span>
                    </button>
                </>
            ) : (
                <>
                    <button
                        type="button"
                        className="btn btn--medium btn--text btn--send"
                        disabled
                    >
                        <span className="btn__span">Отчет отправлен</span>
                    </button>
                </>
            );
        }

        if (!isPast) {
            return (
                <>
                    <SignUp
                        id={id}
                        ignorePrivateCode
                        inProgress={inProgress}
                        onError={this.onError}
                    />

                    <AnnotationAgeLimit
                        ageLimit={age_limit}
                        id={id}
                        organizer={organizer}
                    />

                    {genderDoesNotMatch && (
                        <p className="c-gs-disabled">
                            {`Организатор разрешил запись на игру только для ${
                                getFromGenders().find(
                                    (gender) => gender.code === players_gender
                                )?.name
                            }`}
                        </p>
                    )}

                    {isOrganizer && (
                        <GameControls
                            id={id}
                            privateCode={private_code}
                            isOrganizer={isOrganizer}
                            gameLink={gameLink}
                            gameType={gameType}
                            isTraining={is_training}
                            city_code={city.code}
                        />
                    )}
                </>
            );
        }

        return null;
    };

    selectUser = (userId) => {
        if (this.state.showPlayground) {
            this.toggleShowPlayground();
        }

        this.setState({ showUser: userId });

        let url = new URL(document.location.href);
        url.searchParams.set("player", userId);

        this.props.history.push(url.pathname + url.search);
    };

    animateSidebar = (callback) => {
        document.body.classList.remove(SIDEBAR_ANIMATION_CLASS_OPENED);
        document.body.classList.add(SIDEBAR_ANIMATION_CLASS_CLOSED);

        setTimeout(() => {
            document.body.classList.remove(SIDEBAR_ANIMATION_CLASS_CLOSED);
            callback();
        }, SIDEBAR_ANIMATION_DURATION);
    };

    toggleShowPlayground = () => {
        if (this.state.showUser) {
            this.dropSelectedUser();
        }

        this.animateSidebar(() => {
            this.setState(({ showPlayground }) => ({
                showPlayground: !showPlayground,
            }));
        });
    };

    dropSelectedUser = () => {
        this.setState({ showUser: undefined });

        this.animateSidebar(() => {
            let url = new URL(document.location.href);
            url.searchParams.delete("player");

            this.props.history.push(url.pathname + url.search);
        });
    };

    renderSidebar = () => {
        const { id, playground_id, isOrganizer } = this.props;

        const { showUser, showPlayground } = this.state;
        const isCustomPhone = isOrganizer && "Номер телефона";

        if (!showUser && !showPlayground) {
            return null;
        }

        document.body.classList.add(SIDEBAR_ANIMATION_CLASS_OPENED);

        return (
            <>
                <ErrorBoundary>
                    <>
                        {showPlayground && (
                            <PlaygroundLoader id={playground_id}>
                                <ErrorBoundary>
                                    <PlaygroundDetails
                                        key={playground_id}
                                        playgroundId={playground_id}
                                        onClose={this.toggleShowPlayground}
                                        modalTitle="Площадка"
                                    />
                                </ErrorBoundary>
                            </PlaygroundLoader>
                        )}
                        {showUser && (
                            <PlayerLoader gameId={id} playerId={showUser}>
                                <ErrorBoundary>
                                    <UserDetails
                                        isCustomFields={[isCustomPhone]}
                                        key={showUser}
                                        playerId={showUser}
                                        onClose={this.dropSelectedUser}
                                    />
                                </ErrorBoundary>
                            </PlayerLoader>
                        )}
                    </>
                </ErrorBoundary>
            </>
        );
    };

    renderNotifications = () => {
        const {
            unread_notifications,
            type_id,
            is_public,
            cancel_reason,
            is_participate,
            organizer,
            id,
            starts_at,
            playground,
        } = this.props;

        return (
            <div className="notifications">
                {Array.isArray(unread_notifications) &&
                    unread_notifications.map((item, key) => {
                        return (
                            <ErrorBoundary key={key}>
                                <Item
                                    notification={{
                                        ...item,
                                        game: {
                                            id,
                                            starts_at,
                                            type_id,
                                            is_public,
                                            cancel_reason,
                                            is_participate,
                                            city_id: playground.city_id,
                                            organizer,
                                            playground,
                                        },
                                    }}
                                    short
                                />
                            </ErrorBoundary>
                        );
                    })}
            </div>
        );
    };

    renderRightCol = () => {
        const {
            organizer,
            starts_at,
            duration,
            playground,
            cost,
            currency,
            isOrganizer,
            isDesktop,
            need_report,
            is_training,
            trainer,
            level,
            paymentMethod,
            isPaySuccess,
            is_available_for_pay,
            is_participate,
        } = this.props;

        const { isPast } = this.state;

        return (
            <div className="card-full__right-col">
                {!isDesktop && this.renderNotifications()}
                <ConditionsFirst
                    startsAt={starts_at}
                    duration={duration}
                    playground={playground}
                    handleClickPlayground={this.toggleShowPlayground}
                    currency={currency}
                    cost={cost}
                    level={level}
                    paymentMethod={paymentMethod}
                    isPaySuccess={isPaySuccess}
                />
                {!isOrganizer && (
                    <PersonPreview
                        {...organizer}
                        mods={[
                            `card-full__organizer`,
                            `hide-desktop-down`,
                            `card__person`,
                        ]}
                        with_position
                        onClick={this.selectUser}
                    />
                )}
                {is_training && trainer && (
                    <PersonPreview
                        {...trainer}
                        mods={[
                            `card-full__organizer`,
                            `hide-desktop-down`,
                            `card__person`,
                        ]}
                        with_position
                        position="Тренер"
                        onClick={this.selectUser}
                    />
                )}
                <div
                    className={classNames(
                        paymentMethod === "Возможно онлайн" &&
                            is_available_for_pay &&
                            is_participate
                            ? "card-full__buttons_two"
                            : "card-full__buttons",
                        {
                            "card-full__buttons_with-text": !is_available_for_pay,
                        }
                    )}
                >
                    {this.renderButtons()}
                </div>

                {isPast && !isOrganizer && isDesktop && (
                    <button
                        type="button"
                        className="btn btn--medium btn--text btn--send"
                        style={{ padding: "20px" }}
                        disabled
                    >
                        <span className="btn__span">
                            {need_report
                                ? "Организатор не отправил отчет"
                                : "Организатор отправил отчет"}
                        </span>
                    </button>
                )}
            </div>
        );
    };

    render() {
        const {
            gameType,
            genderName,
            gameTypeImg,
            level,
            organizer,
            id,
            starts_at,
            description,
            playground,
            isLoggedIn,
            need_report,
            players,
            players_count,
            max_players,
            isOrganizer,
            isMobile,
            isDesktop,
            is_public,
            is_training,
            trainer,
            invitations,
            players_lists,
            isTablet,
            isTrainer,
        } = this.props;

        const { showUser, isPast, showPlayground, error } = this.state;
        const sidebarActive = showPlayground || showUser;
        const style = { "--bg-image": `url(${gameTypeImg})` };
        const playersInStack =
            isPast && isLoggedIn
                ? players?.filter((player) => player?.is_came)
                : players?.filter((player) => !player?.is_reserve);
        const isCanMakeReport = isOrganizer || isTrainer;
        const today = Luxon.now();
        const startDate = Luxon.fromSQL(starts_at);
        const isShowNewsletter =
            startDate.diff(today, "hours")?.hours >= HOURS_BEFORE_GAME &&
            isOrganizer &&
            !isPast &&
            is_public;

        if (error) {
            return (
                <InternalServerError
                    returlLinkTitle="Вернуться к игре"
                    onClickButton={this.hideError}
                />
            );
        }

        return (
            <main className="page page--card-full page--no-header">
                <div className="page__wrapper">
                    <section className="card-full content" style={style}>
                        <div className="card-full__head" id="card-header">
                            <div
                                className={cn("card-full__back", {
                                    " game-controls": isMobile || isTablet,
                                })}
                            >
                                <Link
                                    to="#"
                                    onClick={this.handleGoBack}
                                    className="icon-container"
                                >
                                    <Icon
                                        type="back"
                                        inheritColor
                                        inheritSize
                                    />
                                </Link>
                            </div>

                            <div className="card-full__top">
                                <p className="c-white mb-4-16">
                                    {is_training ? "Тренировка" : "Игра"}
                                </p>

                                <h2 className="card-full__title">
                                    {gameType?.name}
                                </h2>
                            </div>

                            <ConditionsSecond
                                mods={[`hide-tablet-down`]}
                                level={level}
                                genderName={genderName}
                                isPublic={is_public}
                            />
                        </div>

                        <div
                            className={classNames(
                                `card-full__body card-full__body--grid`,
                                !sidebarActive &&
                                    `card-full__body--grid-desktop`
                            )}
                        >
                            <ConditionsSecond
                                mods={[`hide-tablet-up`]}
                                level={level}
                                genderName={genderName}
                                isPublic={is_public}
                            />

                            {!sidebarActive && this.renderRightCol()}

                            {isPast && !isOrganizer && !isDesktop && (
                                <button
                                    type="button"
                                    className="btn btn--medium btn--text btn--send"
                                    style={{ padding: "20px 10px" }}
                                    disabled
                                >
                                    <span className="btn__span">
                                        {need_report
                                            ? "Организатор не отправил отчет"
                                            : "Организатор отправил отчет"}
                                    </span>
                                </button>
                            )}

                            {!isDesktop && isShowNewsletter && (
                                <Newsletter gameId={id} gameData={this.props} />
                            )}

                            <div className="card-full__left-col">
                                {isDesktop && this.renderNotifications()}

                                {isDesktop && isShowNewsletter && (
                                    <Newsletter
                                        gameId={id}
                                        gameData={this.props}
                                    />
                                )}

                                {is_training && trainer && (
                                    <PersonPreview
                                        {...trainer}
                                        mods={[
                                            `card-full__organizer`,
                                            `hide-desktop-up`,
                                            `card__person`,
                                        ]}
                                        with_position
                                        position="Тренер"
                                        onClick={this.selectUser}
                                    />
                                )}

                                {description && (
                                    <div className="card-full__desc">
                                        <h3 className="card-full__subtitle">
                                            Описание{" "}
                                            {is_training
                                                ? "тренировки"
                                                : "организатора"}
                                        </h3>

                                        <div className="card-full__text-content">
                                            {description}
                                        </div>
                                    </div>
                                )}

                                {!isOrganizer && (
                                    <div className="card-full__organizer hide-desktop-up">
                                        <h3 className="card-full__subtitle hide-tablet-down ">
                                            Организатор
                                        </h3>

                                        <PersonPreview
                                            {...organizer}
                                            mods={[`card__person`]}
                                            with_position
                                            onClick={this.selectUser}
                                        />
                                    </div>
                                )}

                                <div className="card-full__bottom">
                                    {players_count > 0 && (
                                        <>
                                            <h4 className="fs-h3 hide-tablet-down mb-32">
                                                Игроки
                                            </h4>

                                            <PlayersRange
                                                players_count={players_count}
                                                max_players={max_players}
                                                is_past={isPast}
                                                is_report_sent={!need_report}
                                                is_training={is_training}
                                            />

                                            <PlayersStack
                                                players={playersInStack}
                                                players_count={players_count}
                                                max_players={max_players}
                                                isPast={isPast}
                                            />
                                        </>
                                    )}

                                    {isLoggedIn && !isPast && (
                                        <PlayersList
                                            players={players}
                                            max_players={max_players}
                                            gameId={id}
                                            onPlayerClick={this.selectUser}
                                        />
                                    )}

                                    {!isPast && isOrganizer && (
                                        <GameDetailsInvitations
                                            gameId={id}
                                            invitations={invitations}
                                            invitedGroups={players_lists}
                                            isInvate={true}
                                        />
                                    )}

                                    {isPast && isLoggedIn && (
                                        <GroupListLoader>
                                            <BlackListLoader>
                                                <PlayersReport
                                                    mainCast={players}
                                                    gameId={id}
                                                    needReport={need_report}
                                                    isOrganizer={
                                                        isCanMakeReport
                                                    }
                                                    startsAt={starts_at}
                                                    onPlayerClick={
                                                        this.selectUser
                                                    }
                                                    selectedUser={showUser}
                                                />
                                            </BlackListLoader>
                                        </GroupListLoader>
                                    )}

                                    <div className="card-full__map">
                                        {!isMobile && (
                                            <h4 className="fs-h3 hide-tablet-down mb-24">
                                                Где находится площадка
                                            </h4>
                                        )}
                                        <h4 />
                                        <p className="mb-16">
                                            <div>{playground?.name}</div>
                                            <div className="fs-caption c-gs-label">
                                                {playground?.address}
                                            </div>
                                        </p>

                                        <YMapAddress
                                            placeMarks={[
                                                {
                                                    coords: [
                                                        playground?.latitude,
                                                        playground?.longitude,
                                                    ],
                                                },
                                            ]}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    <div className="sidebar-right">{this.renderSidebar()}</div>
                </div>
            </main>
        );
    }
}

GameDetails.defaultProps = {
    need_report: false,
    is_past: false,
};

GameDetails.propTypes = {
    ...gameTypes,
    shouldLoad: PropTypes.bool,
    isLoggedIn: PropTypes.bool,
    gameType: gameTypeTypes,
    genderName: PropTypes.string,
    closed: PropTypes.bool,
    inProgress: PropTypes.bool,
    is_participate: PropTypes.bool,
    gameTypeImg: PropTypes.string,
    level: levelTypes,
    isOrganizer: PropTypes.bool,
    need_report: PropTypes.bool,
    isMobile: PropTypes.bool,
    gameLink: PropTypes.string.isRequired,
    history: PropTypes.shape({ push: PropTypes.func.isRequired }),
    recall: PropTypes.func.isRequired,
    city: cityTypes,
    unread_notifications: PropTypes.arrayOf(notificationsTypes),
    setViewed: PropTypes.func.isRequired,
    players_lists: PropTypes.array,
    profile: PropTypes.object,
    currency: PropTypes.string,
};

export default GameDetails;
